/**
 * Unified HTML cleanup for editor attributes.
 *
 * Merges logic from:
 *   - PPT wrapper's `cleanEditorAttributes` (per-page subtree cleanup)
 *   - Content editor's `cleanHtmlForSave` + `removeEditorAttributes` (full-document cleanup)
 *
 * Both consumers call `cleanEditorAttributes(root)` on a DOM subtree,
 * which removes all editor-injected attributes, classes, toolbar DOM,
 * formula metadata, and editor scripts/styles.
 */

const EDITOR_ATTRS = [
  'data-musk-text-editable',
  'data-musk-element-id',
  'contenteditable',
] as const

const EDITOR_ROOT_ATTRS = [
  'data-musk-theme',
  'data-musk-theme-mode',
] as const

const FORMULA_ATTRS = [
  'data-musk-formula',
  'data-musk-latex',
  'data-musk-display',
] as const

const EDITOR_CLASSES = [
  'musk-editor-hover',
  'musk-editor-editing',
  'musk-formula-hover',
  'musk-formula-editing',
] as const

const TOOLBAR_ATTR = 'data-musk-toolbar'
const EDITOR_STYLE_ATTR = 'data-musk-editor-style'
const EDITOR_THEME_STYLE_ATTR = 'data-musk-editor-theme-style'
const EDITOR_SCRIPT_ATTR = 'data-musk-editor'
const PREVIEW_RUNTIME_REPORTER_ATTR = 'data-musk-preview-runtime-reporter'

/**
 * Detect whether a <style> or <script> element was injected by the editor.
 */
function isEditorInjected(el: Element): boolean {
  const content = el.textContent || ''
  const src = el.getAttribute('src') || ''
  const href = el.getAttribute('href') || ''

  return (
    content.indexOf('Musk Editor') !== -1
    || content.indexOf('musk-editor-hover') !== -1
    || content.indexOf('data-musk-text-editable') !== -1
    || content.indexOf('musk-style-toolbar') !== -1
    || src.indexOf('musk-html-editor.js') !== -1
    || src.indexOf('musk-preview-runtime-reporter.js') !== -1
    || href.indexOf('musk-html-editor.css') !== -1
    || el.hasAttribute(EDITOR_STYLE_ATTR)
    || el.hasAttribute(EDITOR_THEME_STYLE_ATTR)
    || el.hasAttribute(EDITOR_SCRIPT_ATTR)
    || el.hasAttribute(PREVIEW_RUNTIME_REPORTER_ATTR)
  )
}

/**
 * Clean all editor-injected attributes, classes, toolbar DOM,
 * formula metadata, and editor scripts/styles from a DOM subtree.
 *
 * Works on both a page-fragment (PPT per-page `<div>`) and a full
 * `document.body` (content editor).
 */
export function cleanEditorAttributes(root: Element): void {
  // 1. Remove toolbar DOM elements
  root.querySelectorAll(`[${TOOLBAR_ATTR}]`).forEach((el) => el.remove())

  // 2. Remove editor attributes from all tagged elements
  for (const attr of EDITOR_ATTRS) {
    root.querySelectorAll(`[${attr}]`).forEach((el) => el.removeAttribute(attr))
  }

  // 3. Clean formula elements (preserve MathJax rendered output)
  root.querySelectorAll('mjx-container[data-musk-formula]').forEach((el) => {
    for (const attr of FORMULA_ATTRS) {
      el.removeAttribute(attr)
    }
    el.removeAttribute('data-musk-element-id')
    for (const cls of EDITOR_CLASSES) {
      el.classList.remove(cls)
    }
  })

  // 4. Remove remaining formula attrs on non-mjx elements
  root.querySelectorAll('[data-musk-formula]').forEach((el) => {
    for (const attr of FORMULA_ATTRS) {
      el.removeAttribute(attr)
    }
  })

  // 5. Remove editor class names
  const classSelector = EDITOR_CLASSES.map((c) => `.${c}`).join(', ')
  root.querySelectorAll(classSelector).forEach((el) => {
    for (const cls of EDITOR_CLASSES) {
      el.classList.remove(cls)
    }
  })

  // 6. Restore disabled attributes removed during editing
  root.querySelectorAll('[data-musk-was-disabled]').forEach((el) => {
    el.setAttribute('disabled', '')
    el.removeAttribute('data-musk-was-disabled')
  })

  // 7. Clean empty class/style attributes
  root.querySelectorAll('[class], [style]').forEach((el) => {
    if (el.getAttribute('class') === '') el.removeAttribute('class')
    if (el.getAttribute('style') === '') el.removeAttribute('style')
  })

  // 8. Remove editor-injected <style> and <script> tags
  root.querySelectorAll('style, script').forEach((el) => {
    if (isEditorInjected(el)) el.remove()
  })
}

/**
 * Clean editor scripts/styles from <head>.
 * Used by content-editor's full-document save path.
 */
export function cleanEditorHead(head: Element): void {
  head.querySelectorAll('style, script, link').forEach((el) => {
    if (isEditorInjected(el)) el.remove()
  })
}

export function cleanEditorRoot(root: Element): void {
  for (const attr of EDITOR_ROOT_ATTRS) {
    root.removeAttribute(attr)
  }
}
